import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { IconMinus, IconPlus, IconHeartFilled, IconSkull, IconSettings } from '@tabler/icons-react';
import { useStore } from 'stores/rootStore';
import { IPlayer, getGradientByIndex, baseColors, additionalColors } from 'stores/appStore';
import Modal from 'components/modal/Modal';
import CommanderModal from 'components/commander/CommanderModal';
import CommanderCardsGrid from 'components/commander/CommanderCardsGrid';
import Button from 'components/button/Button';
import Fireworks from 'components/fireworks/Fireworks';
import PlayerSettingsModal from 'components/player/PlayerSettingsModal';
import styles from './PlayerCard.css';

interface Props {
    player: IPlayer;
    rotate?: number;
    index?: number;
    totalPlayers?: number;
}

const PlayerCard = observer(({ player, rotate = 0, index = 0, totalPlayers = 4 }: Props) => {
    const { app } = useStore();
    const cardRef = useRef<HTMLDivElement>(null);
    const innerContainerRef = useRef<HTMLDivElement>(null);
    const [delta, setDelta] = useState(0);
    const [deltaVisible, setDeltaVisible] = useState(false);
    const [animationPhase, setAnimationPhase] = useState<'idle' | 'delta-entering' | 'delta-visible' | 'delta-exiting' | 'heart-entering'>('idle');
    const leftSideOverlayRef = useRef<HTMLDivElement>(null);
    const rightSideOverlayRef = useRef<HTMLDivElement>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [commanderModalVisible, setCommanderModalVisible] = useState(false);
    const [showWinnerFireworks, setShowWinnerFireworks] = useState(false);
    const [playerSettingsModalVisible, setPlayerSettingsModalVisible] = useState(false);
    const [negativeHealthMode, setNegativeHealthMode] = useState(false);

    const isOnTheSide = rotate === 90 || rotate === 270;
    const playerIndex = typeof index === 'number' ? index : parseInt(player.id, 10) - 1;

    const updateDelta = (change: number) => {
        setDelta(prev => {
            const newDelta = prev + change;
            
            // Only start entering animation if not already visible
            if (animationPhase === 'idle' || animationPhase === 'heart-entering') {
                setAnimationPhase('delta-entering');
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setAnimationPhase('delta-visible');
                    });
                });
            }
            setDeltaVisible(true);

            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }

            resetTimeoutRef.current = setTimeout(() => {
                setAnimationPhase('delta-exiting');
                setDeltaVisible(false);
                setTimeout(() => {
                    setAnimationPhase('heart-entering');
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setDelta(0);
                            setAnimationPhase('idle');
                        });
                    });
                }, 200);
            }, 1000);

            return newDelta;
        });
    };

    const animateLeftFeedback = () => {
        if (leftSideOverlayRef.current) {
            leftSideOverlayRef.current.style.opacity = '1';
            setTimeout(() => {
                if (leftSideOverlayRef.current) {
                    leftSideOverlayRef.current.style.opacity = '0';
                }
            }, 150);
        }
    };

    const animateRightFeedback = () => {
        if (rightSideOverlayRef.current) {
            rightSideOverlayRef.current.style.opacity = '1';
            setTimeout(() => {
                if (rightSideOverlayRef.current) {
                    rightSideOverlayRef.current.style.opacity = '0';
                }
            }, 150);
        }
    };

    const onPressDecreaseHealth = () => {
        animateLeftFeedback();
        app.decreaseHealth(player.id, 1);
        updateDelta(-1);
    };

    const onPressIncreaseHealth = () => {
        animateRightFeedback();
        app.increaseHealth(player.id, 1);
        updateDelta(1);
    };

    const onLongPressDecreaseHealth = () => {
        animateLeftFeedback();
        app.decreaseHealth(player.id, 10);
        updateDelta(-10);
    };

    const onLongPressIncreaseHealth = () => {
        animateRightFeedback();
        app.increaseHealth(player.id, 10);
        updateDelta(10);
    };

    const hasLethalCommanderDamage = (): boolean => {
        return player.commanderDamage.some(damage => damage.damage >= 21 && !damage.isMe);
    };

    const isDead = player.health <= 0 || hasLethalCommanderDamage();

    const isWinner = (): boolean => {
        if (isDead) return false;
        return app.players.every(p => {
            if (p.id === player.id) return true;
            return p.health <= 0 || p.commanderDamage.some(damage => damage.damage >= 21 && !damage.isMe);
        });
    };

    useEffect(() => {
        const winner = isWinner();
        if (winner && !showWinnerFireworks) {
            setShowWinnerFireworks(true);
        }
    }, [...app.players.map(p => p.health), ...app.players.flatMap(p => p.commanderDamage.map(cd => cd.damage))]);

    useEffect(() => {
        if (!isDead && negativeHealthMode) {
            setNegativeHealthMode(false);
        }
    }, [player.health, isDead, negativeHealthMode]);

    const handleFireworksEnd = () => {
        setShowWinnerFireworks(false);
    };

    const gradientColors = getGradientByIndex(index);
    const gradientStyle = {
        background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
    };

    // Get the animation direction style based on rotation
    // Enter: from below, moving up (20px -> 0px in visual space)
    // Exit: moving up and out (0px -> -20px in visual space)
    // Need to adjust for card rotation since elements are counter-rotated
    const getEnterTransform = () => {
        // Starting position for enter animation (from below)
        switch (rotate) {
            case 0: return 'translateY(20px)';
            case 90: return 'translateX(-20px)';
            case 180: return 'translateY(-20px)';
            case 270: return 'translateX(20px)';
            default: return 'translateY(20px)';
        }
    };

    const getExitTransform = () => {
        // End position for exit animation (moving up and out)
        switch (rotate) {
            case 0: return 'translateY(-20px)';
            case 90: return 'translateX(20px)';
            case 180: return 'translateY(20px)';
            case 270: return 'translateX(-20px)';
            default: return 'translateY(-20px)';
        }
    };

    const getDeltaAnimationStyle = () => {
        const transition = 'opacity 0.2s ease, transform 0.2s ease';
        
        switch (animationPhase) {
            case 'delta-entering':
                // Starting position - no transition yet
                return { opacity: 0, transform: getEnterTransform(), transition: 'none' };
            case 'delta-visible':
                // Animate to center
                return { opacity: 1, transform: 'translate(0, 0)', transition };
            case 'delta-exiting':
                // Animate out
                return { opacity: 0, transform: getExitTransform(), transition };
            default:
                // Hidden at exit position
                return { opacity: 0, transform: getExitTransform(), transition: 'none' };
        }
    };

    const getHeartAnimationStyle = () => {
        const transition = 'opacity 0.2s ease, transform 0.2s ease';
        
        switch (animationPhase) {
            case 'delta-entering':
                // Heart starts visible, about to exit
                return { opacity: 1, transform: 'translate(0, 0)', transition: 'none' };
            case 'delta-visible':
                // Heart exits - animate up and out
                return { opacity: 0, transform: getExitTransform(), transition };
            case 'delta-exiting':
                // Heart hidden at exit position while delta exits
                return { opacity: 0, transform: getExitTransform(), transition: 'none' };
            case 'heart-entering':
                // Heart starts at enter position (below), ready to animate in
                return { opacity: 0, transform: getEnterTransform(), transition: 'none' };
            default:
                // Heart visible at center - animate in from enter position
                return { opacity: 1, transform: 'translate(0, 0)', transition };
        }
    };

    // For rotated cards (90 or 270 degrees), we need to swap width and height
    const getInnerContainerStyle = () => {
        if (isOnTheSide) {
            return {
                position: 'absolute' as const,
                width: '100%',
                height: '100%',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
                // Swap the aspect ratio for the rotated content
                aspectRatio: 'auto',
            };
        }
        return { transform: `rotate(${rotate}deg)` };
    };

    return (
        <div className={styles.card} ref={cardRef}>
            <div className={styles.gradientBackground} style={gradientStyle} />
            <div 
                className={styles.innerContainer} 
                ref={innerContainerRef}
                style={getInnerContainerStyle()}
            >
                {/* Negative health button */}
                {isDead && !negativeHealthMode && (
                    <div className={styles.negativeHealthButtonContainer}>
                        <Button 
                            className={styles.negativeHealthButton}
                            onPress={() => setNegativeHealthMode(true)}
                        >
                            <span className={styles.negativeHealthButtonText}>Negative Health</span>
                        </Button>
                    </div>
                )}

                {/* Fireworks */}
                <Fireworks visible={showWinnerFireworks} onAnimationEnd={handleFireworksEnd} duration={5000} />

                {/* Left side - decrease */}
                <Button 
                    className={styles.leftSide}
                    onPress={onPressDecreaseHealth}
                    onLongPress={onLongPressDecreaseHealth}
                    activeOpacity={1}
                >
                    <div className={styles.sideOverlay} ref={leftSideOverlayRef}>
                        <div className={styles.leftGradient} />
                    </div>
                    <IconMinus 
                        size={42}
                        className={styles.sideIcon}
                        style={{ 
                            right: '15%',
                            color: isDead && negativeHealthMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)'
                        }}
                    />
                </Button>

                {/* Right side - increase */}
                <Button 
                    className={styles.rightSide}
                    onPress={onPressIncreaseHealth}
                    onLongPress={onLongPressIncreaseHealth}
                    activeOpacity={1}
                >
                    <div className={styles.sideOverlay} ref={rightSideOverlayRef}>
                        <div className={styles.rightGradient} />
                    </div>
                    <IconPlus 
                        size={42}
                        className={styles.sideIcon}
                        style={{ 
                            left: '15%',
                            color: isDead && negativeHealthMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)'
                        }}
                    />
                </Button>

                {/* Header container with heart and delta */}
                <div 
                    className={styles.headerContainer}
                    style={{ transform: `rotate(${-rotate}deg)` }}
                >
                    {/* Heart icon */}
                    {(!isDead || negativeHealthMode) && (
                        <div 
                            className={styles.deltaContainer}
                            style={getHeartAnimationStyle()}
                        >
                            <div style={{ transform: `rotate(${rotate}deg)` }}>
                                {isDead ? (
                                    <IconSkull 
                                        size={24}
                                        style={{ color: isDead && negativeHealthMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)' }}
                                    />
                                ) : (
                                    <IconHeartFilled 
                                        size={24}
                                        style={{ color: 'rgba(255,255,255,0.9)' }}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Delta text */}
                    {delta !== 0 && (!isDead || negativeHealthMode) && (
                        <div 
                            className={styles.deltaContainer}
                            style={getDeltaAnimationStyle()}
                        >
                            <div style={{ transform: `rotate(${rotate}deg)` }}>
                                <span 
                                    className={styles.deltaText}
                                    style={{ color: isDead && negativeHealthMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)' }}
                                >
                                    {delta < 0 ? delta : `+${delta}`}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Health display */}
                {(!isDead || negativeHealthMode) && (
                    <div className={styles.centerContainer}>
                        <span 
                            className={styles.lifeText}
                            style={{ color: isDead && negativeHealthMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)' }}
                        >
                            {player.health}
                        </span>
                    </div>
                )}

                {/* Commander Damage Area */}
                <div className={styles.commandersContainer}>
                    <Button
                        className={styles.commandersContainerButton}
                        onPress={() => setCommanderModalVisible(true)}
                        activeOpacity={0.7}
                    >
                        <CommanderCardsGrid
                            playerRotation={rotate}
                            commanderDamage={player.commanderDamage as any}
                            onIncrease={(id: string, amount: number) => player.increaseCommanderDamage(id, amount)}
                            onDecrease={(id: string, amount: number) => player.decreaseCommanderDamage(id, amount)}
                            commanderDeaths={player.commanderDeaths}
                            onIncreaseDeaths={() => player.increaseCommanderDeaths()}
                            onDecreaseDeaths={() => player.decreaseCommanderDeaths()}
                        />
                    </Button>
                </div>

                {/* Dead overlay */}
                {isDead && (
                    <div className={styles.deadOverlay}>
                        {!negativeHealthMode && (
                            <IconSkull size={40} style={{ color: 'rgba(255,255,255,0.8)', marginTop: -25 }} />
                        )}
                    </div>
                )}

                {/* Commander Modal */}
                <CommanderModal
                    visible={commanderModalVisible}
                    onClose={() => setCommanderModalVisible(false)}
                    playerRotation={rotate}
                    commanderDamage={player.commanderDamage as any}
                    onIncrease={(id: string, amount: number) => player.increaseCommanderDamage(id, amount)}
                    onDecrease={(id: string, amount: number) => player.decreaseCommanderDamage(id, amount)}
                    commanderDeaths={player.commanderDeaths}
                    onIncreaseDeaths={() => player.increaseCommanderDeaths()}
                    onDecreaseDeaths={() => player.decreaseCommanderDeaths()}
                />

                {/* Player Settings Modal */}
                <Modal
                    visible={playerSettingsModalVisible}
                    onClose={() => setPlayerSettingsModalVisible(false)}
                    modalStyle={{ maxWidth: '90%', maxHeight: '80%' }}
                >
                    <PlayerSettingsModal
                        player={player}
                        baseColors={baseColors}
                        additionalColors={additionalColors}
                        onClose={() => setPlayerSettingsModalVisible(false)}
                    />
                </Modal>
            </div>
        </div>
    );
});

export default PlayerCard;
